/**
 * moonshot-cart-validation.js
 *
 * Client-side mirror of the Shopify Cart Validation Function
 * (apps/moonshot-functions/extensions/cart-validation/src/index.js).
 *
 * WHY THIS EXISTS:
 *   When the Function returns errors at the /cart → /checkouts
 *   transition, Shopify redirects back to /cart silently — neither
 *   `cart.errors` nor `cart.cart_level_errors` is populated in Liquid
 *   on the next render. The customer just sees their cart again with
 *   no idea what went wrong. We mirror the same rules in browser JS
 *   so we can:
 *     1. Render the rule message inline above the Checkout button.
 *     2. Disable the Checkout button when errors exist (no more
 *        bouncy click).
 *
 * The Function remains the server-side source of truth. This file is
 * presentation only — if it disagrees with the Function (bug,
 * stale rules) the Function still wins at submit time.
 *
 * Data source: a `<script type="application/json"
 * data-moonshot-cart-data>` block emitted by snippets/moonshot-cart-errors.liquid
 * containing per-line fulfillment + product-metafield values.
 *
 * Loaded via layout/theme.liquid after moonshot-cart-error-persistence.js.
 */
(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const VALID_PICKUP_GROUPS = ["noblesville", "plainfield"];

  // Per-page cache of enriched cart data (cart-data JSON + per-line
  // stock-by-location). Populated lazily after the first evaluate().
  // Reset on cart-changed events.
  let _enrichedCache = null;
  // Tracks whether the async stock fetch has completed at least once.
  // The first evaluate() runs synchronously off the raw Liquid JSON
  // (no stock filter); after the stock fetch resolves, evaluate()
  // runs again with the enriched data.
  let _stockFetched = false;

  function readCartData() {
    const node = document.querySelector("script[data-moonshot-cart-data]");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || "{}");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[moonshot-cart-validation] cart data parse failed:", err);
      return null;
    }
  }

  /**
   * Reads cart data, returning the stock-enriched version when it's
   * been fetched, or the raw Liquid JSON otherwise. The fields
   * `stockNoblesville` and `stockPlainfield` are only present after
   * the App Proxy fetch resolves.
   */
  function getCurrentCartData() {
    if (_enrichedCache) return _enrichedCache;
    return readCartData();
  }

  /**
   * Fetches per-location stock for each unique variant in the cart via
   * the moonshot stock App Proxy (already used by the PDP widget).
   * Mutates _enrichedCache so subsequent evaluate() calls use the
   * stock-aware bulk + per-line option logic.
   *
   * Failure modes: if the proxy is down or returns non-JSON, falls back
   * to eligibility-only filtering (unchanged from pre-stock behavior).
   * Stock data is not load-bearing for validation correctness — it only
   * tightens which OPTIONS we present in the resolution UI. The
   * cart-validation Function and the v42 delivery-customization safety
   * net still catch any bad state at submit.
   */
  async function fetchStockForCart() {
    const raw = readCartData();
    if (!raw || !Array.isArray(raw.lines)) return;

    const variantIds = [];
    for (const ln of raw.lines) {
      if (ln && ln.id && variantIds.indexOf(String(ln.id)) === -1) {
        variantIds.push(String(ln.id));
      }
    }

    /** @type {Object<string, { noblesville: number, plainfield: number }>} */
    const stockByVariant = {};

    await Promise.all(
      variantIds.map(async (vid) => {
        try {
          const res = await fetch(
            "/apps/moonshot/stock?variant=" + encodeURIComponent(vid),
            { credentials: "same-origin", headers: { Accept: "application/json" } },
          );
          if (!res.ok) return;
          const data = await res.json();
          const byGroup = (data && data.byGroup) || {};
          stockByVariant[vid] = {
            noblesville: typeof byGroup.noblesville === "number" ? byGroup.noblesville : 0,
            plainfield: typeof byGroup.plainfield === "number" ? byGroup.plainfield : 0,
          };
        } catch (err) {
          // Swallow — fall back to eligibility-only filtering for this line.
          // eslint-disable-next-line no-console
          console.warn("[moonshot-cart-validation] stock fetch failed for", vid, err);
        }
      }),
    );

    _enrichedCache = Object.assign({}, raw, {
      lines: raw.lines.map(function (ln) {
        const s = stockByVariant[String(ln.id)] || { noblesville: 0, plainfield: 0 };
        return Object.assign({}, ln, {
          stockNoblesville: s.noblesville,
          stockPlainfield: s.plainfield,
        });
      }),
    });
    _stockFetched = true;
  }

  /**
   * Mirror of cart-validation/src/index.js rules. Returns an array of
   * unique error messages (in the same wording the Function uses).
   */
  function validateCart(cartData) {
    const errors = [];
    if (!cartData || !Array.isArray(cartData.lines) || cartData.lines.length === 0) {
      return errors;
    }
    const lines = cartData.lines;

    // Pass 1: per-line resolution + cart-wide aggregates
    const resolved = [];
    const pickupGroupsInCart = [];
    const streamFulfillments = [];

    for (const line of lines) {
      const fulfillment = line.fulfillment || "ship";
      const isStream = line.isStream === "true";
      const canShip = line.canShip !== "false";
      const canPickup = line.canPickup !== "false";
      const title = line.title || "this item";

      resolved.push({ fulfillment, isStream, canShip, canPickup, title });

      if (fulfillment.indexOf("pickup:") === 0) {
        const group = fulfillment.slice("pickup:".length);
        if (
          VALID_PICKUP_GROUPS.indexOf(group) !== -1 &&
          pickupGroupsInCart.indexOf(group) === -1
        ) {
          pickupGroupsInCart.push(group);
        }
      }

      if (isStream && streamFulfillments.indexOf(fulfillment) === -1) {
        streamFulfillments.push(fulfillment);
      }
    }

    function pushUnique(msg) {
      if (errors.indexOf(msg) === -1) errors.push(msg);
    }

    // Pass 2: emit errors
    for (const r of resolved) {
      // Rule 6: unknown pickup group
      if (r.fulfillment.indexOf("pickup:") === 0) {
        const group = r.fulfillment.slice("pickup:".length);
        if (VALID_PICKUP_GROUPS.indexOf(group) === -1) {
          pushUnique(
            "Sorry — that pickup isn't available. Pick Noblesville or Plainfield.",
          );
          continue;
        }
      }

      // Rule 1: stream-only at Plainfield
      if (r.isStream && r.fulfillment === "pickup:plainfield") {
        pushUnique(
          "Moonshot Rips packs are only opened on stream from Noblesville. Choose ship or pickup at Noblesville for " +
            r.title +
            ".",
        );
        continue;
      }

      // Rule 2: ship-required on a non-shippable item
      if (!r.canShip && r.fulfillment === "ship") {
        pushUnique(
          r.title + " is pickup-only — please choose pickup at Noblesville or Plainfield.",
        );
        continue;
      }

      // Rule 3: pickup chosen on a no-pickup item
      if (!r.canPickup && r.fulfillment.indexOf("pickup:") === 0) {
        pushUnique(r.title + " can't be picked up — please switch it to ship.");
        continue;
      }
    }

    // Rule 4: multiple stream items must share fulfillment
    let streamLineCount = 0;
    for (const r of resolved) if (r.isStream) streamLineCount++;
    if (streamLineCount > 1 && streamFulfillments.length > 1) {
      pushUnique(
        "Your Moonshot Rips items need to share a fulfillment method. Pick the same option (ship or pickup at Noblesville) for all of them.",
      );
    }

    // Rule 5: pickup split across two stores
    if (pickupGroupsInCart.length > 1) {
      pushUnique(
        "Your pickup items are split between two stores. Pick one location for the whole order.",
      );
    }

    return errors;
  }

  /**
   * Returns the list of fulfillment options that EVERY line in the cart
   * could legally accept. These are safe to offer as bulk "apply to
   * everything" buttons that resolve cross-line constraints (rules 4 + 5)
   * without creating new per-line violations (rules 1, 2, 3) or
   * stock-availability mismatches at the chosen location.
   *
   * Stock fields (stockNoblesville / stockPlainfield) are only present
   * after fetchStockForCart() resolves. When they're undefined we fall
   * back to eligibility-only filtering — same behavior as before this
   * stock-aware revision. This keeps the initial render snappy while
   * the proxy call is in flight.
   */
  function computeBulkOptions(lines) {
    const candidates = ["pickup:noblesville", "pickup:plainfield", "ship"];
    const out = [];
    for (const opt of candidates) {
      const allOk = lines.every((line) => {
        const isStream = line.isStream === "true";
        const canShip = line.canShip !== "false";
        const canPickup = line.canPickup !== "false";
        const stockN = typeof line.stockNoblesville === "number" ? line.stockNoblesville : null;
        const stockP = typeof line.stockPlainfield === "number" ? line.stockPlainfield : null;

        if (opt === "ship") {
          if (!canShip) return false;
          // Ship draws from Noblesville stock today (per project memory:
          // PDP widget uses the same convention until non-stream
          // inventory lands at non-pickup locations). If we know stock
          // and Noblesville is empty, ship isn't viable for this line.
          if (stockN !== null && stockN === 0) return false;
          return true;
        }
        if (opt === "pickup:noblesville") {
          if (!canPickup) return false;
          if (stockN !== null && stockN === 0) return false;
          return true;
        }
        if (opt === "pickup:plainfield") {
          if (!canPickup) return false;
          if (isStream) return false;
          if (stockP !== null && stockP === 0) return false;
          return true;
        }
        return false;
      });
      if (allOk) out.push(opt);
    }
    return out;
  }

  function labelForOption(opt) {
    if (opt === "ship") return "Ship everything";
    if (opt === "pickup:noblesville") return "Pickup all at Noblesville";
    if (opt === "pickup:plainfield") return "Pickup all at Plainfield";
    return opt;
  }

  function labelForPerLineOption(opt) {
    if (opt === "ship") return "Ship to me";
    if (opt === "pickup:noblesville") return "Pick up at Noblesville";
    if (opt === "pickup:plainfield") return "Pick up at Plainfield";
    return opt;
  }

  /**
   * Returns the fulfillment options that this single line could legally
   * accept. Used when the cart has irreconcilable items and bulk fix
   * isn't possible — we surface a per-line selector instead, gated by
   * each product's eligibility metafields AND its per-location stock
   * (when stock data is available — see fetchStockForCart).
   */
  function allowedOptionsForLine(line) {
    const isStream = line.isStream === "true";
    const canShip = line.canShip !== "false";
    const canPickup = line.canPickup !== "false";
    const stockN = typeof line.stockNoblesville === "number" ? line.stockNoblesville : null;
    const stockP = typeof line.stockPlainfield === "number" ? line.stockPlainfield : null;
    const out = [];
    if (canShip && (stockN === null || stockN > 0)) out.push("ship");
    if (canPickup && (stockN === null || stockN > 0)) out.push("pickup:noblesville");
    if (canPickup && !isStream && (stockP === null || stockP > 0)) {
      out.push("pickup:plainfield");
    }
    return out;
  }

  function renderErrors(errors) {
    const container = document.querySelector("[data-moonshot-cart-errors]");
    const list = document.querySelector("[data-moonshot-cart-errors-list]");
    if (!container || !list) return;

    if (errors.length === 0) {
      container.setAttribute("hidden", "");
      list.innerHTML = "";
      hideBulkFixes();
      hidePerLineFixes();
      return;
    }

    list.innerHTML = "";
    for (const msg of errors) {
      const li = document.createElement("li");
      li.textContent = msg;
      list.appendChild(li);
    }
    container.removeAttribute("hidden");
    renderResolutionUI();
  }

  function hideBulkFixes() {
    const fixes = document.querySelector("[data-moonshot-cart-fixes]");
    if (fixes) fixes.setAttribute("hidden", "");
  }

  function hidePerLineFixes() {
    const perline = document.querySelector("[data-moonshot-cart-perline]");
    if (perline) perline.setAttribute("hidden", "");
  }

  /**
   * Decides whether to surface bulk-fix buttons (preferred — keeps
   * fulfillment uniform across the order) or per-line selectors
   * (fallback when no single option works for every line). Never
   * shows both: per the product spec, bulk wins when available so
   * the fulfillment team only ever sees one fulfillment per order
   * unless the cart truly demands otherwise.
   */
  function renderResolutionUI() {
    const data = getCurrentCartData();
    const lines = (data && data.lines) || [];
    if (lines.length === 0) {
      hideBulkFixes();
      hidePerLineFixes();
      return;
    }

    const bulkOptions = computeBulkOptions(lines);
    if (bulkOptions.length > 0) {
      renderBulkFixes(bulkOptions);
      hidePerLineFixes();
    } else {
      hideBulkFixes();
      renderPerLineFixes(lines);
    }
  }

  function renderBulkFixes(options) {
    const fixes = document.querySelector("[data-moonshot-cart-fixes]");
    const buttonsHost = document.querySelector("[data-moonshot-cart-fix-buttons]");
    if (!fixes || !buttonsHost) return;

    buttonsHost.innerHTML = "";
    for (const opt of options) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "moonshot-cart-errors__fix-button";
      btn.textContent = labelForOption(opt);
      btn.dataset.moonshotFulfillment = opt;
      btn.addEventListener("click", () => applyBulkFulfillment(opt));
      buttonsHost.appendChild(btn);
    }
    fixes.removeAttribute("hidden");
  }

  function renderPerLineFixes(lines) {
    const perline = document.querySelector("[data-moonshot-cart-perline]");
    const listHost = document.querySelector("[data-moonshot-cart-perline-list]");
    if (!perline || !listHost) return;

    listHost.innerHTML = "";
    for (const line of lines) {
      const allowed = allowedOptionsForLine(line);
      if (allowed.length === 0) continue;

      const li = document.createElement("li");
      li.className = "moonshot-cart-errors__perline-row";

      const titleEl = document.createElement("span");
      titleEl.className = "moonshot-cart-errors__perline-title";
      titleEl.textContent = line.title || "Item";
      li.appendChild(titleEl);

      // If the line only has one valid option, show it as static text —
      // no select needed.
      if (allowed.length === 1) {
        const onlyEl = document.createElement("span");
        onlyEl.className = "moonshot-cart-errors__perline-title";
        onlyEl.style.flex = "0 0 auto";
        onlyEl.style.fontWeight = "500";
        onlyEl.style.color = "#777";
        onlyEl.textContent = labelForPerLineOption(allowed[0]) + " (only option)";
        li.appendChild(onlyEl);
      } else {
        const select = document.createElement("select");
        select.className = "moonshot-cart-errors__perline-select";
        select.dataset.moonshotLineKey = line.key;
        for (const opt of allowed) {
          const optionEl = document.createElement("option");
          optionEl.value = opt;
          optionEl.textContent = labelForPerLineOption(opt);
          if ((line.fulfillment || "ship") === opt) optionEl.selected = true;
          select.appendChild(optionEl);
        }
        select.addEventListener("change", (e) => {
          const next = e.target.value;
          applyPerLineFulfillment(line.key, next);
        });
        li.appendChild(select);
      }

      listHost.appendChild(li);
    }
    perline.removeAttribute("hidden");
  }

  function setPerLineStatus(message, isError) {
    const status = document.querySelector("[data-moonshot-cart-perline-status]");
    if (!status) return;
    if (!message) {
      status.setAttribute("hidden", "");
      status.textContent = "";
      return;
    }
    status.textContent = message;
    status.style.color = isError ? "#b8311f" : "#777";
    status.removeAttribute("hidden");
  }

  function setPerLineSelectsDisabled(disabled) {
    document
      .querySelectorAll(".moonshot-cart-errors__perline-select")
      .forEach((sel) => {
        if (disabled) sel.setAttribute("disabled", "");
        else sel.removeAttribute("disabled");
      });
  }

  /**
   * Updates a single line's fulfillment property. Preserves the line's
   * other line-item properties.
   */
  async function applyPerLineFulfillment(lineKey, option) {
    setPerLineSelectsDisabled(true);
    setPerLineStatus("Updating that item...", false);

    // Engage the escape hatch — per-line edits can also land in
    // still-invalid intermediate states (e.g., two pickup locations
    // remaining after the first line is changed) which the function
    // would otherwise reject.
    await setSkipValidation(true);

    let cart;
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      cart = await res.json();
    } catch (err) {
      await setSkipValidation(false);
      setPerLineSelectsDisabled(false);
      setPerLineStatus("Couldn't read your cart. Please refresh and try again.", true);
      return;
    }

    const items = cart.items || [];
    const idx = items.findIndex((it) => it.key === lineKey);
    if (idx === -1) {
      await setSkipValidation(false);
      setPerLineSelectsDisabled(false);
      setPerLineStatus("Couldn't find that item in your cart. Please refresh.", true);
      return;
    }
    const item = items[idx];
    const newProps = buildPropsForFulfillment(item, option);
    const result = await changeLineProperties(idx + 1, newProps);

    if (!result.ok) {
      const detail = describeChangeFailure(result);
      // eslint-disable-next-line no-console
      console.error(
        "[moonshot-cart-validation] line",
        idx + 1,
        "(" + (item.title || item.product_title) + ") update failed:",
        result,
      );
      await setSkipValidation(false);
      setPerLineSelectsDisabled(false);
      setPerLineStatus("Couldn't update that item: " + detail, true);
      return;
    }

    await setSkipValidation(false);
    setPerLineStatus("Updated. Refreshing...", false);
    window.location.reload();
  }

  function setFixStatus(message, isError) {
    const status = document.querySelector("[data-moonshot-cart-fix-status]");
    if (!status) return;
    if (!message) {
      status.setAttribute("hidden", "");
      status.textContent = "";
      return;
    }
    status.textContent = message;
    status.style.color = isError ? "#b8311f" : "#777";
    status.removeAttribute("hidden");
  }

  function setFixButtonsDisabled(disabled) {
    document
      .querySelectorAll(".moonshot-cart-errors__fix-button")
      .forEach((btn) => {
        if (disabled) btn.setAttribute("disabled", "");
        else btn.removeAttribute("disabled");
      });
  }

  /**
   * Sets or clears the bulk-update escape hatch cart attribute. The
   * cart-validation Function honors a `moonshot_skip_validation_at`
   * attribute as long as its timestamp is within the last 30 seconds
   * — gives us a window to flip every line through /cart/change.js
   * without each intermediate state tripping rule 4 / rule 5.
   *
   * Always paired: setSkipValidation(true) → updates → setSkipValidation(false).
   * Pass `false` to clear (the function ignores empty strings).
   */
  async function setSkipValidation(active) {
    const value = active ? String(Date.now()) : "";
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          attributes: { moonshot_skip_validation_at: value },
        }),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[moonshot-cart-validation] skip-validation toggle failed:", err);
    }
  }

  /**
   * Posts a single line-property update to /cart/change.js. Uses the
   * 1-indexed `line` parameter instead of the line key — keys can shift
   * after any prior update in a sequence, line numbers stay stable.
   * Returns the response body (parsed JSON if possible, raw text on
   * non-JSON, null on network error).
   */
  async function changeLineProperties(lineNumber, newProps) {
    let res;
    try {
      res = await fetch("/cart/change.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ line: lineNumber, properties: newProps }),
      });
    } catch (err) {
      return { ok: false, status: 0, body: String(err) };
    }
    const text = await res.text();
    let body = text;
    try {
      body = JSON.parse(text);
    } catch (e) {
      // leave as raw text
    }
    return { ok: res.ok, status: res.status, body };
  }

  function describeChangeFailure(failure) {
    if (!failure) return "unknown error";
    if (typeof failure.body === "object" && failure.body) {
      return (
        failure.body.description ||
        failure.body.message ||
        ("HTTP " + failure.status)
      );
    }
    return failure.body || "HTTP " + failure.status;
  }

  /**
   * Builds the new properties dict for a line item: preserves every
   * existing property except the fulfillment keys, then writes the new
   * fulfillment under `fulfillment` (no underscore). Drops the legacy
   * `_fulfillment` key on every write.
   */
  function buildPropsForFulfillment(item, option) {
    const newProps = {};
    if (item.properties) {
      for (const k of Object.keys(item.properties)) {
        if (k === "fulfillment" || k === "_fulfillment") continue;
        newProps[k] = item.properties[k];
      }
    }
    newProps.fulfillment = option;
    return newProps;
  }

  function currentFulfillment(item) {
    if (!item || !item.properties) return "ship";
    return item.properties.fulfillment || item.properties._fulfillment || "ship";
  }

  /**
   * Applies the chosen fulfillment to every line in the cart that
   * doesn't already match. Preserves each line's other line-item
   * properties (so things like stream_handle don't get clobbered).
   */
  async function applyBulkFulfillment(option) {
    setFixButtonsDisabled(true);
    setFixStatus("Updating your cart...", false);

    // Engage the escape hatch BEFORE the first /cart/change.js so the
    // cart-validation Function lets each intermediate state through.
    // The 30-second auto-expire on the function side prevents a stale
    // flag from disabling validation indefinitely.
    await setSkipValidation(true);

    let cart;
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      cart = await res.json();
    } catch (err) {
      await setSkipValidation(false);
      setFixButtonsDisabled(false);
      setFixStatus("Couldn't read your cart. Please refresh and try again.", true);
      return;
    }

    const items = (cart && cart.items) || [];
    if (items.length === 0) {
      await setSkipValidation(false);
      setFixButtonsDisabled(false);
      setFixStatus("Your cart is empty.", false);
      return;
    }

    // Loop by line number so subsequent iterations don't depend on
    // potentially-changed line keys. Skip lines already at `option`.
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (currentFulfillment(item) === option) continue;

      const newProps = buildPropsForFulfillment(item, option);
      const result = await changeLineProperties(i + 1, newProps);

      if (!result.ok) {
        const detail = describeChangeFailure(result);
        // eslint-disable-next-line no-console
        console.error(
          "[moonshot-cart-validation] line",
          i + 1,
          "(" + (item.title || item.product_title) + ") update failed:",
          result,
        );
        await setSkipValidation(false);
        setFixButtonsDisabled(false);
        setFixStatus(
          "Couldn't update " +
            (item.product_title || item.title || "item " + (i + 1)) +
            ": " +
            detail,
          true,
        );
        return;
      }
    }

    // Clear the escape hatch BEFORE reloading so any subsequent cart
    // mutations on the new page hit normal validation immediately.
    // The final cart state should be valid; if it's not, the next
    // /cart/change.js or /checkouts attempt will surface the problem.
    await setSkipValidation(false);

    setFixStatus("Cart updated. Refreshing...", false);
    // Full reload is the simplest way to re-render every part of the
    // page that depends on cart state (this snippet's JSON, the cart
    // line-item properties shown to the customer, the moonshot footer,
    // anything else).
    window.location.reload();
  }

  function blockCheckoutForms(hasErrors) {
    const buttons = document.querySelectorAll('button[name="checkout"]');
    buttons.forEach((btn) => {
      const form = btn.closest("form");
      if (!form) return;
      if (hasErrors) {
        form.classList.add("moonshot-cart-blocked");
      } else {
        form.classList.remove("moonshot-cart-blocked");
      }
    });
  }

  function attachSubmitGuards(errors) {
    const buttons = document.querySelectorAll('button[name="checkout"]');
    buttons.forEach((btn) => {
      const form = btn.closest("form");
      if (!form) return;
      // We add the listener once; the closure captures the latest
      // error-state lookup so updates after cart edits are honored.
      if (form.dataset.moonshotGuardAttached === "1") return;
      form.dataset.moonshotGuardAttached = "1";
      form.addEventListener(
        "submit",
        (e) => {
          // Re-validate at submit time in case the cart changed since
          // page load (line-item quantity changes, attribute updates).
          const data = getCurrentCartData();
          const liveErrors = validateCart(data);
          if (liveErrors.length > 0) {
            e.preventDefault();
            renderErrors(liveErrors);
            blockCheckoutForms(true);
            const banner = document.querySelector("[data-moonshot-cart-errors]");
            if (banner && banner.scrollIntoView) {
              banner.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }
        },
        true,
      );
    });
  }

  function evaluate() {
    const data = getCurrentCartData();
    const errors = validateCart(data);
    renderErrors(errors);
    blockCheckoutForms(errors.length > 0);
    attachSubmitGuards(errors);
  }

  /**
   * Async helper: fetch stock for the current cart and re-render. Safe
   * to call multiple times; the cache is keyed by call-time so cart-
   * changed events trigger fresh fetches.
   */
  async function refreshStockAndEvaluate() {
    _enrichedCache = null;
    _stockFetched = false;
    try {
      await fetchStockForCart();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[moonshot-cart-validation] stock refresh failed:", err);
    }
    evaluate();
  }

  function start() {
    // First render runs synchronously off the Liquid JSON — eligibility-
    // only filtering. Snappy, may briefly show options that the stock
    // pass below will remove.
    evaluate();

    // Async pass: enrich with per-location stock from the App Proxy,
    // then re-evaluate so bulk + per-line selectors don't offer options
    // that would push the cart into an unfulfillable state.
    refreshStockAndEvaluate();

    // Re-evaluate when other moonshot scripts dispatch cart-changed events
    // (quantity changes, line removal, fulfillment-attribute updates).
    document.addEventListener("moonshot:cart:changed", refreshStockAndEvaluate);
    // The Warehouse theme re-renders cart sections via Section Rendering API
    // — we listen for theme:cart:render events too.
    document.addEventListener("theme:cart:render", refreshStockAndEvaluate);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
